/**
 * OnboardingChecklistPanel — Phase 2F (Feb 2026)
 * ---------------------------------------------------------------------------
 * Coordinator-facing onboarding checklist. Pure UI presentation layer on top
 * of the Phase 2E Readiness Engine + two read-only lookups (audit logs and
 * the participant dashboard counter for shifts).
 *
 * Mount modes:
 *   mode="full"    — Participant Detail page "Onboarding" tab. Full 14-row
 *                    checklist grouped by category.
 *   mode="banner"  — Intake Wizard preview strip. 12 readiness rows only,
 *                    rendered compact and collapsed by default. Operational
 *                    rows (welcome, first shift) are hidden because they only
 *                    become meaningful AFTER activation.
 *
 * Feature flag: `participant_onboarding_checklist_enabled` (default OFF).
 * Renders nothing when the flag is OFF (hard client-side gate).
 *
 * v1 boundaries — STRICT:
 *   • Purely informational. No row blocks any workflow.
 *   • No new backend endpoint. No new collection. No schema migration.
 *   • No manual tick / untick. No row state stored on the server.
 *   • No automatic enforcement, no auto-status change on the participant.
 *   • Reuses Phase 2E `/readiness` response verbatim for 12 rows.
 *   • Welcome row reads `/audit?entityId=...&action=participant.welcome.sent`.
 *   • First-shift row reads `/participants/{id}/dashboard.active_shifts`.
 *     Both extras fail gracefully (403 / 404 / network) → row is rendered as
 *     "Not available" without bricking the checklist.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CheckCircle2, AlertCircle, HelpCircle, RefreshCw, ClipboardCheck,
  ChevronDown, ChevronUp, Users, ShieldAlert, IdCard, Mail, Rocket,
} from 'lucide-react';

const FLAG_KEY = 'participant_onboarding_checklist_enabled';
const READINESS_FLAG_KEY = 'participant_readiness_engine_enabled';

// Coordinator-facing status labels. Neutral wording — never "blocked".
const STATUS_LABEL = {
  COMPLETE:       'Complete',
  NEEDS_ATTENTION:'Needs attention',
  TODO:           'Not yet completed',
  NOT_APPLICABLE: 'Not applicable',
  UNAVAILABLE:    'Not available',
};

const STATUS_TONE = {
  COMPLETE:       'bg-emerald-100 text-emerald-800 border-emerald-300',
  NEEDS_ATTENTION:'bg-amber-100 text-amber-800 border-amber-300',
  TODO:           'bg-slate-100 text-slate-700 border-slate-300',
  NOT_APPLICABLE: 'bg-slate-100 text-slate-500 border-slate-200',
  UNAVAILABLE:    'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_ICON = {
  COMPLETE:       <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  NEEDS_ATTENTION:<AlertCircle className="h-4 w-4 text-amber-600" />,
  TODO:           <HelpCircle className="h-4 w-4 text-slate-500" />,
  NOT_APPLICABLE: <HelpCircle className="h-4 w-4 text-slate-400" />,
  UNAVAILABLE:    <HelpCircle className="h-4 w-4 text-slate-400" />,
};

// Map a Phase 2E check status + required flag into a coordinator-facing status.
function mapReadinessStatus(check) {
  switch (check.status) {
    case 'PASS':           return 'COMPLETE';
    case 'FAIL':           return check.required ? 'TODO' : 'NEEDS_ATTENTION';
    case 'NOT_APPLICABLE': return 'NOT_APPLICABLE';
    case 'INCONCLUSIVE':
    default:               return 'UNAVAILABLE';
  }
}

// Per-row category. Defined upfront so banner mode can filter by category too.
const CATEGORIES = [
  { id: 'identity',   label: 'Identity & Funding',   icon: <IdCard className="h-3.5 w-3.5" /> },
  { id: 'safety',     label: 'Safety & Clinical',    icon: <ShieldAlert className="h-3.5 w-3.5" /> },
  { id: 'team',       label: 'Team & Service',       icon: <Users className="h-3.5 w-3.5" /> },
  { id: 'consent',    label: 'Consent & Welcome',    icon: <Mail className="h-3.5 w-3.5" /> },
  { id: 'launch',     label: 'Service Launch',       icon: <Rocket className="h-3.5 w-3.5" /> },
];

// Map each readiness check key to a category and human label.
// Labels intentionally re-cast from "is X done?" (readiness language) into
// "X complete" (coordinator task language).
const READINESS_ROW_DEFS = {
  demographics_complete:                { category: 'identity', label: 'Demographics captured' },
  funding_plan_active:                  { category: 'identity', label: 'NDIS / funding plan active' },
  service_agreement_signed:             { category: 'identity', label: 'Service Agreement signed' },
  required_documents_uploaded:          { category: 'identity', label: 'Required documents on file' },
  risk_assessment_complete:             { category: 'safety',   label: 'Risk assessment complete' },
  emergency_contact_present:            { category: 'safety',   label: 'Emergency contact provided' },
  restrictive_practices_review_current: { category: 'safety',   label: 'Restrictive-practice review current' },
  anaphylaxis_alert_acknowledged:       { category: 'safety',   label: 'Anaphylaxis acknowledged (if applicable)' },
  coordinator_assigned:                 { category: 'team',     label: 'Coordinator assigned' },
  service_allocations:                  { category: 'team',     label: 'Service allocations set' },
  support_worker_assigned:              { category: 'team',     label: 'Support worker matched' },
  consents_signed:                      { category: 'consent',  label: 'Consents signed (privacy + SA)' },
};

// Stable display order — mirrors Phase 2E + appends the 2 operational rows.
const ROW_ORDER = [
  'demographics_complete',
  'funding_plan_active',
  'service_agreement_signed',
  'required_documents_uploaded',
  'risk_assessment_complete',
  'emergency_contact_present',
  'restrictive_practices_review_current',
  'anaphylaxis_alert_acknowledged',
  'coordinator_assigned',
  'service_allocations',
  'support_worker_assigned',
  'consents_signed',
  '__welcome_communication_sent',
  '__first_shift_scheduled',
];

function readFlag(key) {
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    return !!flags[key];
  } catch {
    return false;
  }
}

// ─── Compose the rows from the readiness response + 2 extras ─────────────────
function composeRows(readiness, extras) {
  const rowsByKey = {};

  // 12 readiness-derived rows.
  for (const c of readiness?.checks || []) {
    const def = READINESS_ROW_DEFS[c.key];
    if (!def) continue;
    rowsByKey[c.key] = {
      key: c.key,
      label: def.label,
      category: def.category,
      status: mapReadinessStatus(c),
      hint: c.hint || '',
      required: !!c.required,
      source: 'readiness_engine',
    };
  }

  // Operational row 1 — welcome communication sent.
  rowsByKey.__welcome_communication_sent = {
    key: '__welcome_communication_sent',
    label: 'Welcome communication sent',
    category: 'consent',
    status: extras.welcome.status,
    hint: extras.welcome.hint,
    required: false,
    source: 'audit_logs',
  };

  // Operational row 2 — first shift scheduled.
  rowsByKey.__first_shift_scheduled = {
    key: '__first_shift_scheduled',
    label: 'First shift scheduled',
    category: 'launch',
    status: extras.firstShift.status,
    hint: extras.firstShift.hint,
    required: false,
    source: 'shifts',
  };

  return ROW_ORDER.map(k => rowsByKey[k]).filter(Boolean);
}


// ─── Sub-components ─────────────────────────────────────────────────────────
function VerdictHeader({ verdict, summary, generatedAt, completeCount, totalCount, onRefresh }) {
  const tone = verdict === 'READY'
    ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
    : verdict === 'READY_WITH_WARNINGS'
      ? 'bg-amber-50 border-amber-300 text-amber-900'
      : verdict === 'NOT_READY'
        ? 'bg-orange-50 border-orange-300 text-orange-900'
        : 'bg-slate-50 border-slate-300 text-slate-700';
  const headline = verdict === 'READY'
    ? 'Onboarding complete'
    : verdict === 'READY_WITH_WARNINGS'
      ? 'Onboarding mostly complete'
      : verdict === 'NOT_READY'
        ? 'Onboarding in progress'
        : 'Onboarding status unavailable';
  return (
    <div
      className={`rounded-md border px-3 py-2 flex items-center justify-between gap-3 ${tone}`}
      data-testid="onboarding-verdict-header"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <ClipboardCheck className="h-4 w-4" />
        <span className="font-semibold text-sm" data-testid="onboarding-verdict-label">
          {headline}
        </span>
        <span className="text-xs opacity-80" data-testid="onboarding-progress-summary">
          · {completeCount}/{totalCount} rows complete
        </span>
        {summary?.warning_failed > 0 && (
          <span className="text-xs opacity-80">
            · {summary.warning_failed} need attention
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {generatedAt && (
          <span className="text-[10px] opacity-60 hidden sm:inline">
            {new Date(generatedAt).toLocaleString()}
          </span>
        )}
        {onRefresh && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onRefresh}
            data-testid="onboarding-refresh-btn"
            className="h-7"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}


function ChecklistRow({ row }) {
  const icon = STATUS_ICON[row.status] || STATUS_ICON.UNAVAILABLE;
  const tone = STATUS_TONE[row.status] || STATUS_TONE.UNAVAILABLE;
  const label = STATUS_LABEL[row.status] || row.status;
  return (
    <li
      className="flex items-start gap-2 py-1.5"
      data-testid={`onboarding-row-${row.key}`}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{row.label}</span>
          <Badge variant="outline" className={`${tone} text-[10px] px-1.5`}>
            {label}
          </Badge>
        </div>
        {row.hint && (
          <p
            className="text-xs text-slate-600 mt-0.5"
            data-testid={`onboarding-row-hint-${row.key}`}
          >
            {row.hint}
          </p>
        )}
      </div>
    </li>
  );
}


function CategoryGroup({ category, rows }) {
  if (rows.length === 0) return null;
  return (
    <div data-testid={`onboarding-category-${category.id}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1.5">
        {category.icon}
        <span>{category.label}</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map(r => <ChecklistRow key={r.key} row={r} />)}
      </ul>
    </div>
  );
}


// ─── Hook: gather all data the checklist needs ──────────────────────────────
function useChecklistData(participantId, { includeExtras }) {
  const [readiness, setReadiness] = useState(null);
  const [extras, setExtras] = useState({
    welcome:    { status: 'UNAVAILABLE', hint: '' },
    firstShift: { status: 'UNAVAILABLE', hint: '' },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!participantId) { setReadiness(null); return; }
    setLoading(true);
    setError(null);

    // ── Phase 2E readiness — the source of truth for 12 rows.
    let r = null;
    try {
      const res = await api.get(`/participants/${participantId}/readiness`);
      r = res.data?.data || null;
      setReadiness(r);
    } catch (e) {
      const code = e.response?.data?.error?.code || e.response?.data?.detail?.code;
      if (code === 'FEATURE_DISABLED') {
        // Phase 2E flag is OFF — checklist cannot meaningfully render the 12 rows.
        setError('READINESS_DISABLED');
        setReadiness(null);
        setLoading(false);
        return;
      }
      setError(e.response?.data?.error?.message || e.message || 'Failed to load readiness');
      setLoading(false);
      return;
    }

    // ── Welcome communication + first shift — best-effort, never fatal.
    if (includeExtras) {
      const nextExtras = {
        welcome:    { status: 'TODO', hint: 'No welcome communication recorded yet.' },
        firstShift: { status: 'TODO', hint: 'No shift scheduled yet.' },
      };

      // Welcome — query audit logs filtered by entity + action.
      try {
        const res = await api.get('/audit', {
          params: {
            entityId: participantId,
            action: 'participant.welcome.sent',
            page: 1,
            limit: 1,
          },
        });
        const items = res.data?.data || res.data?.items || [];
        if (Array.isArray(items) && items.length > 0) {
          const ts = items[0]?.timestamp || items[0]?.created_at;
          nextExtras.welcome = {
            status: 'COMPLETE',
            hint: ts ? `Sent ${new Date(ts).toLocaleDateString()}.` : 'Sent.',
          };
        }
      } catch (e) {
        // 403 → coordinator lacks audit.read; mark unavailable, never fatal.
        nextExtras.welcome = {
          status: 'UNAVAILABLE',
          hint: 'Welcome communication evidence not visible in your role.',
        };
      }

      // First shift — read participant dashboard `active_shifts` counter.
      try {
        const res = await api.get(`/participants/${participantId}/dashboard`);
        const count = res.data?.data?.active_shifts ?? 0;
        if (count > 0) {
          nextExtras.firstShift = {
            status: 'COMPLETE',
            hint: `${count} upcoming or in-progress shift${count === 1 ? '' : 's'} scheduled.`,
          };
        }
      } catch (e) {
        nextExtras.firstShift = {
          status: 'UNAVAILABLE',
          hint: 'Shift evidence not visible in your role.',
        };
      }

      setExtras(nextExtras);
    }

    setLoading(false);
  }, [participantId, includeExtras]);

  useEffect(() => { load(); }, [load]);

  return { readiness, extras, loading, error, reload: load };
}


// ─── Main component ────────────────────────────────────────────────────────
export function OnboardingChecklistPanel({
  participantId,
  mode = 'full',
  intake = null,
}) {
  // Hard client-side flag gate — no API call when flag is OFF.
  const [flagEnabled, setFlagEnabled] = useState(null);
  const [readinessFlagEnabled, setReadinessFlagEnabled] = useState(null);
  const [expanded, setExpanded] = useState(mode === 'full');

  useEffect(() => {
    setFlagEnabled(readFlag(FLAG_KEY));
    setReadinessFlagEnabled(readFlag(READINESS_FLAG_KEY));
  }, []);

  const effectivePid = mode === 'banner'
    ? (intake?.promoted_participant_id || null)
    : (participantId || null);

  // Banner mode: only 12 readiness rows (welcome + first shift only meaningful post-activation).
  const includeExtras = mode === 'full';

  const { readiness, extras, loading, error, reload } = useChecklistData(
    flagEnabled === true ? effectivePid : null,
    { includeExtras }
  );

  // ── Flag OFF → render nothing.
  if (flagEnabled !== true) return null;

  // ── Banner mode pre-activation → tasteful placeholder.
  if (mode === 'banner' && !effectivePid) {
    return (
      <Card
        className="border-dashed bg-slate-50/50"
        data-testid="onboarding-panel-intake-preactivation"
      >
        <CardContent className="p-3 flex items-center gap-2 text-xs text-slate-600">
          <ClipboardCheck className="h-3.5 w-3.5 text-slate-500" />
          <span>
            The onboarding checklist becomes available once the participant is activated.
            It is informational only and does not block activation or service delivery.
          </span>
        </CardContent>
      </Card>
    );
  }

  // ── Readiness flag dependency.
  if (readinessFlagEnabled !== true || error === 'READINESS_DISABLED') {
    return (
      <Card
        className="border-dashed bg-slate-50/50"
        data-testid="onboarding-panel-readiness-disabled"
      >
        <CardContent className="p-3 text-xs text-slate-600">
          Onboarding checklist requires the Readiness Engine. Ask an administrator to
          enable <code className="font-mono text-[10px]">participant_readiness_engine_enabled</code>.
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card data-testid={`onboarding-panel-${mode}-loading`}>
        <CardContent className="p-3 space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200" data-testid={`onboarding-panel-${mode}-error`}>
        <CardContent className="p-3 text-xs text-red-700">
          Could not load onboarding checklist: {error}
        </CardContent>
      </Card>
    );
  }

  if (!readiness) return null;

  const rows = composeRows(readiness, extras);
  const completeCount = rows.filter(r => r.status === 'COMPLETE').length;
  const totalCount = rows.length;
  const verdict = readiness.verdict;

  // ── Banner mode (Intake Wizard preview).
  if (mode === 'banner') {
    return (
      <Card
        className="bg-white/50"
        data-testid="onboarding-panel-banner"
      >
        <CardContent className="p-3 space-y-2">
          <VerdictHeader
            verdict={verdict}
            summary={readiness.summary}
            generatedAt={readiness.generated_at}
            completeCount={completeCount}
            totalCount={totalCount}
            onRefresh={reload}
          />
          <div
            className="text-[11px] text-slate-500"
            data-testid="onboarding-informational-disclaimer"
          >
            Informational only — does not block shifts or service delivery in v1.
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded(!expanded)}
            className="h-7 text-xs"
            data-testid="onboarding-toggle-details"
          >
            {expanded ? (
              <><ChevronUp className="h-3 w-3 mr-1" /> Hide checklist</>
            ) : (
              <><ChevronDown className="h-3 w-3 mr-1" /> Show checklist ({totalCount} rows)</>
            )}
          </Button>
          {expanded && (
            <div className="border-t pt-2 space-y-3" data-testid="onboarding-row-list">
              {CATEGORIES.map(cat => (
                <CategoryGroup
                  key={cat.id}
                  category={cat}
                  rows={rows.filter(r => r.category === cat.id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── Full mode (Participant Detail "Onboarding" tab).
  return (
    <Card className="bg-white" data-testid="onboarding-panel-full">
      <CardContent className="p-3 space-y-3">
        <VerdictHeader
          verdict={verdict}
          summary={readiness.summary}
          generatedAt={readiness.generated_at}
          completeCount={completeCount}
          totalCount={totalCount}
          onRefresh={reload}
        />
        <p
          className="text-[11px] text-slate-500"
          data-testid="onboarding-informational-disclaimer"
        >
          Informational only — does not block shifts or service delivery in v1.
          Auto-derived from system state; no manual ticking in this version.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {CATEGORIES.map(cat => (
            <CategoryGroup
              key={cat.id}
              category={cat}
              rows={rows.filter(r => r.category === cat.id)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default OnboardingChecklistPanel;
